import React from "react";
import { Clock, MapPin, Phone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

export default function HoursWidget({ variant = "default", className = "" }) {
  const { settings } = useSettings();
  const today = new Date().getDay();
  const dayIndex = today === 0 ? 6 : today - 1;
  const hours = settings.hours || [];
  const todayData = hours[dayIndex] || { day: "Today", closed: true };

  if (variant === "compact") {
    return (
      <div
        data-testid="hours-widget-compact"
        className={`inline-flex items-center gap-2 rounded-full bg-sun-100 px-4 py-2 text-sm font-semibold text-leaf-900 ${className}`}
      >
        <Clock className="h-4 w-4" />
        <span>
          {todayData.closed
            ? "Closed today"
            : todayData.note
              ? `Today · ${todayData.note}`
              : `Open today · ${todayData.open} – ${todayData.close}`}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="hours-widget"
      className={`relative overflow-hidden rounded-3xl border-2 border-sun-200 bg-sun-100 p-6 text-leaf-900 ${className}`}
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-sun-300/50 blur-2xl" />
      <div className="relative">
        <div className="mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          <h3 className="font-display text-2xl font-semibold">Shop Hours</h3>
        </div>
        <ul className="space-y-1.5 text-sm sm:text-base">
          {hours.map((h, i) => (
            <li
              key={h.day}
              data-testid={`hours-row-${h.day.toLowerCase()}`}
              className={`flex items-baseline justify-between gap-2 ${
                i === dayIndex
                  ? "rounded-lg bg-white/70 px-2 py-1 font-bold"
                  : "px-2"
              }`}
            >
              <span className="font-semibold uppercase tracking-wide">
                {h.day}
              </span>
              <span className="flex-1 border-b border-dashed border-leaf-900/20 mx-2 translate-y-[-4px]" />
              <span className="tabular-nums">
                {h.closed
                  ? "Closed"
                  : h.note
                    ? h.note
                    : `${h.open} – ${h.close}`}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-5 space-y-2 border-t border-sun-300/50 pt-4 text-sm">
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span data-testid="hours-widget-address">{settings.address}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 shrink-0" />
            <a
              href={`tel:${(settings.phone || "").replace(/\D/g, "")}`}
              data-testid="hours-widget-phone"
              className="hover:underline"
            >
              {settings.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
